import { usePageableTable } from "../../../context/PageableTableContext";
import { memo, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { PageableTable } from "../../../components/table";
import { Link, useNavigate } from "react-router-dom";
import { DateTimeFormat } from "libraries/date-time-format/date-time-format";
import { PageableTableActions } from "../../../states/pageable-table";
import { Predicates } from "../../../libraries/predicates/predicates";
import { ArtworkApprovalActions } from "../state/approvals";
import { useArtworkApprovalContextStore } from "../context/approvals/ArtworkApprovalsContext";
import { ArtworkApprovalRequestTypes } from "../state/approvals/data.type";

const ArtworkApprovalRequestsIdCellRenderer = memo(
  (model: { id: number | string }) => {
    return (
      <div className="d-flex">
        <span className="pe-2 flex-fill align-self-center">{model.id}</span>
        <div></div>
      </div>
    );
  },
);

interface ITableArtworkCollectionRequestProps {
  collections:any, isLoading:boolean
}


export const TableArtworkCollectionRequest = ({collections, isLoading}:ITableArtworkCollectionRequestProps) => {
  const navigate = useNavigate();

  const gridRef = useRef<AgGridReact<Array<any>>>(null);

  const { dispatch: dispatchPageTable, state:{offset} } = usePageableTable();
  const { state, dispatch } = useArtworkApprovalContextStore();


  useEffect(() => {
    dispatchPageTable(PageableTableActions.setLoading(isLoading));
    dispatchPageTable(PageableTableActions.setPageSize(state.collectionFilters.limit));
    if (Predicates.isNotNullAndNotUndefined(collections)) {
      dispatchPageTable(
        PageableTableActions.setTotal(collections?.count),
      );
    }
  }, [collections, dispatchPageTable, isLoading]);

  useEffect(()=>{
    dispatch(
      ArtworkApprovalActions.setStateValue(
        ArtworkApprovalRequestTypes.SET_COLLECTION_FILTERS,
        { ...state.collectionFilters, offset:offset },
      ),
    );  },[offset])

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: "Artwork Level",
        field: "context.id",
        minWidth: 70,
        maxWidth: 140,
        cellRenderer: (params: any) => (
          <ArtworkApprovalRequestsIdCellRenderer
            // @ts-ignore
            id="Collection"
          />
        ),
      },
      {
        headerName: "Code / Name",
        field: "id",
        minWidth: 70,
        cellRenderer: (params: any) => {
          const pt_code =
            params.data.status === "APPROVED"
              ? `${params.data?.data?.decoration_technique?.id.toString().padStart(2, 0)}-${params.data?.corresponding_object_id.toString().padStart(5, 0)}`
              : null;

          return (
            <>
                <ArtworkApprovalRequestsIdCellRenderer
                  id={
                    !!params?.data?.name
                      ? `${params?.data?.code} - ${params?.data?.name}`
                      : params?.data?.code
                  }
                />
            </>
          );
        },
      },
      {
        headerName: "Request Date",
        field: "created_at",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => (
          <ArtworkApprovalRequestsIdCellRenderer
            id={DateTimeFormat.formatTimestamp(params?.data?.created_on)}
          />
        ),
      },
      {
        headerName: "Requestor",
        field: "user_name",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const requestor = params?.data?.created_by

          return (
            <ArtworkApprovalRequestsIdCellRenderer
              id={
                !!requestor
                  ? `${requestor?.first_name} ${requestor?.last_name}`
                  : ""
              }
            />
          );
        },
      },
      {
        headerName: "Requestor Location",
        field: "requestor_location",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const requestor = params?.data?.created_by
          return <>{!!requestor && requestor?.location?.name}</>;
        },
      },
      {
        headerName: "Approved / Rejected Date",
        field: "data.status",
        minWidth: 70,
        maxWidth: 200,
        cellRenderer: (params: any) => {
          const approvedDate = params?.data?.updated_on

          return (
            <>
              {(params?.data?.status.name === "APPROVED" ||
                params?.data?.status.name === "REJECTED") &&
              !!approvedDate
                ? DateTimeFormat.formatTimestamp(approvedDate)
                : ""}
            </>
          );
        },
      },
      {
        headerName: "Approver",
        field: "approver",
        minWidth: 70,
        maxWidth: 140,
        cellRenderer: (params: any) => {
          const approver = params?.data?.updated_by

          return (
            <>
              {(params?.data?.status.name === "APPROVED" ||
                params?.data?.status.name === "REJECTED") &&
              !!approver?.first_name
                ? `${approver?.first_name} ${approver?.last_name}`
                : ""}
            </>
          );
        },
      },
      {
        headerName: "Status",
        field: "status.name",
        minWidth: 70,
        maxWidth: 100,
        cellRenderer: (params: any) => (
          <ArtworkApprovalRequestsIdCellRenderer id={params?.data?.status.name} />
        ),
      },
      {
        headerName: "Actions",
        minWidth: 70,
        maxWidth: 100,
        cellRenderer: (params: any) => {
          return (
            <Link
              className="mt-1 me-3"
              to={`/artwork/collections/detail/${params.data.id}`
              }
              style={{ textDecoration: "underline" }}
              state={params.data}
            >
              View
            </Link>
          );
        },
      },
    ],
    [collections],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      headerClass: "text-wrap",
      minWidth: 100,
      flex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      wrapText: true,
      autoHeight: true,
      suppressMenu: true,
      suppressMovable: true,
      sortable: true,
    }),
    [],
  );

  const tableProp = useMemo(
    () => ({
      gridRef,
      rowData: collections?.results,
      columnDefs,
      defaultColDef,
    }),
    [gridRef, collections, columnDefs, defaultColDef],
  );

  return (
    <>
      <PageableTable tableProp={{ ...tableProp }} />
    </>
  );
};
